import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package, FileText, ShoppingCart, DollarSign, Users, TrendingUp, Plus, Sparkles } from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const statCards = [
    { label: 'Products', value: stats?.products || 0, icon: Package, color: 'text-[#1A9DA5]', bg: 'bg-[#1A9DA5]/10' },
    { label: 'Pages', value: stats?.pages || 0, icon: FileText, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Orders', value: stats?.orders || 0, icon: ShoppingCart, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Revenue', value: `$${(stats?.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Customers', value: stats?.customers || 0, icon: Users, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Guides', value: stats?.guides || 0, icon: TrendingUp, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Outfit']">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back to Wayfinder Guides</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/products/new">
            <Button data-testid="new-product-btn" variant="outline" size="sm"><Plus className="w-4 h-4 mr-1" />New Product</Button>
          </Link>
          <Link to="/admin/products/generate">
            <Button data-testid="ai-generate-btn" size="sm" className="bg-[#E8735A] hover:bg-[#D4614A] text-white">
              <Sparkles className="w-4 h-4 mr-1" />Generate with AI
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((s, i) => (
          <Card key={i} className="border hover:shadow-md transition-shadow" data-testid={`stat-${s.label.toLowerCase()}`}>
            <CardContent className="p-4">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center mb-3`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <p className="text-2xl font-bold font-['Outfit']">{s.value}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg font-['Outfit']">Recent Orders</CardTitle></CardHeader>
          <CardContent>
            {!stats?.recent_orders?.length ? (
              <p className="text-muted-foreground text-sm">No orders yet. Share your guides to start selling!</p>
            ) : (
              <div className="space-y-3">
                {stats.recent_orders.map((o, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div>
                      <p className="text-sm font-medium">{o.customer_email || 'Anonymous'}</p>
                      <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-200">${o.amount}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-[#E8735A]">
          <CardHeader><CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#E8735A]" />Quick Actions</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/products/generate" className="block">
              <div className="p-3 rounded-lg border border-dashed border-[#E8735A]/30 hover:bg-[#E8735A]/5 transition-colors cursor-pointer">
                <p className="text-sm font-medium">Generate a New Guide with AI</p>
                <p className="text-xs text-muted-foreground">Let Wayfinder create a complete audio guide from a simple prompt</p>
              </div>
            </Link>
            <Link to="/admin/products/new" className="block">
              <div className="p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer">
                <p className="text-sm font-medium">Create Product Manually</p>
                <p className="text-xs text-muted-foreground">Build a guide or package step by step</p>
              </div>
            </Link>
            <Link to="/admin/pages" className="block">
              <div className="p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer">
                <p className="text-sm font-medium">Manage Landing Pages</p>
                <p className="text-xs text-muted-foreground">Edit, create, or generate landing pages for your products</p>
              </div>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
